/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Stack, Text, Depths, PrimaryButton, Spinner, SpinnerSize, Icon } from '@fluentui/react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import useStagedTxnReIngestionMutation from '../../hooks/useStagedTxReIngestionMutation';
import theme from '../../../../theme.style';

export default function ContractNotApprovedRecommendation({ data }) {
  const { contract, month, stagedTransactionsBreakdown } = data;
  const stagedTxnReIngestionMutation = useStagedTxnReIngestionMutation();

  const totalStagedTxsThatAreContractNotApproved = useMemo(() => {
    if (isEmpty(stagedTransactionsBreakdown.totalsByErrorReason.ContractNotApproved)) {
      return null;
    }

    return Object.values(
      stagedTransactionsBreakdown.totalsByErrorReason.ContractNotApproved,
    ).reduce((sum, val) => sum + val, 0);
  }, [stagedTransactionsBreakdown]);

  if (contract.approvalStatus.toLowerCase() !== 'approved') {
    return (
      <Stack
        style={{
          display: 'inline-flex',
          backgroundColor: 'white',
          boxShadow: Depths.depth4,
          padding: 20,
        }}
        tokens={{ childrenGap: 14 }}
      >
        <Text style={{ fontWeight: 'bold' }}>Approve the Contract</Text>

        <Text style={{ fontWeight: 'normal' }}>
          Contract status is <span style={{ fontWeight: 'bold' }}>{contract.approvalStatus}</span>{' '}
          and re-ingestion will not work properly until the contract is Approved.
          <br />
          Ensure that the contract is approved before re-ingesting anything. Review the contract{' '}
        </Text>

        {totalStagedTxsThatAreContractNotApproved > 0 && (
          <Text style={{ fontWeight: 'normal' }}>
            There are{' '}
            <span style={{ fontWeight: 'bold' }}>{totalStagedTxsThatAreContractNotApproved}</span>{' '}
            staged transactions with an error reason of{' '}
            <span style={{ fontWeight: 'bold' }}>ContractNotApproved</span>. First approve the
            contract, then come back here to re-ingest the transaction by clicking the submit button
            above again.
          </Text>
        )}

        <PrimaryButton>
          <a
            style={{ color: 'white', textDecoration: 'none' }}
            href={`/contracts/${contract.address}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Review Contract
          </a>
        </PrimaryButton>
      </Stack>
    );
  }

  if (totalStagedTxsThatAreContractNotApproved === 0) {
    return null;
  }

  return (
    <Stack
      style={{
        display: 'inline-flex',
        backgroundColor: 'white',
        boxShadow: Depths.depth4,
        padding: 20,
      }}
      tokens={{ childrenGap: 14 }}
    >
      <Text style={{ fontWeight: 'bold' }}>Re-Ingest ContractNotApproved Staged Transactions</Text>

      <Text style={{ fontWeight: 'normal' }}>
        There are <strong>{totalStagedTxsThatAreContractNotApproved}</strong> staged transactions
        with an ErrorReason of <strong>ContractNotApproved</strong>. These were put there because at
        the time of ingestion the contract was not approved. The contract is now approved, so these
        staged transactions can be re-ingested. Click the button below to re-ingest these staged
        transactions.
      </Text>

      {stagedTxnReIngestionMutation.error && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentError }}>
          {stagedTxnReIngestionMutation.error.response?.data?.message ||
            stagedTxnReIngestionMutation.error.message}
        </Text>
      )}

      {stagedTxnReIngestionMutation.isSuccess && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentTeal }}>
          <Icon iconName="StatusCircleCheckmark" />
          Successfully sent {totalStagedTxsThatAreContractNotApproved} staged transaction with
          ErrorReason of ContractNotApproved to the service bus. Give calc-engine some time to
          process them, then click submit again. If the problem persists, check logs of calc-engine.
        </Text>
      )}

      <PrimaryButton
        disabled={stagedTxnReIngestionMutation.isLoading}
        onClick={() =>
          stagedTxnReIngestionMutation.mutate({
            productIds: Object.keys(
              stagedTransactionsBreakdown.totalsByErrorReason.ContractNotApproved,
            ),
            errorReason: 'ContractNotApproved',
            month,
          })
        }
      >
        {stagedTxnReIngestionMutation.isLoading ? (
          <Spinner size={SpinnerSize.xSmall} />
        ) : (
          'Re-Ingest StagedTxs'
        )}
      </PrimaryButton>
    </Stack>
  );
}

ContractNotApprovedRecommendation.propTypes = {
  data: PropTypes.shape({}).isRequired,
};
